import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import * as turf from '@turf/turf';
import type { Feature, MultiPolygon, Polygon } from 'geojson';
import { Maximize2 } from 'lucide-react';
import maplibregl from 'maplibre-gl';
import type { MapRef } from 'react-map-gl/maplibre';
import { Layer, Map, Source } from 'react-map-gl/maplibre';

import { env } from '@/config/env';
import { MAP_DEFAULTS } from '@/config/map';
import { getVietmapVectorStyleUrl } from '@/config/vietmap';
import {
  lngLatBboxToImageCoordinates,
  mercatorTileToImageCoordinates,
  SAR_PREVIEW_TILE_ZOOM,
  webMercatorTileBoundsLngLat,
} from '@/features/map/utils/alphaEarthSarPreviewTile';
import {
  FLOOD_GEOJSON_STROKE_ALPHA,
  floodRegionPolygonFillRgba,
  floodRgbColorToCssRgba,
  SAR_FLOOD_EXTENT_PREVIEW_NOMINAL_DEPTH_M,
} from '@/features/map/utils/floodDepthScale';

/** Style tối giản cho MapLibre (không dùng `StyleSpecification` từ maplibre-gl). */
const REMOTE_PREVIEW_MAP_STYLE = {
  version: 8 as const,
  name: 'remote-preview',
  sources: {},
  layers: [
    {
      id: 'remote-preview-bg',
      type: 'background',
      paint: { 'background-color': '#0b1220' },
    },
  ],
};

/**
 * AOI preview — đồng bộ `drawn-polygon-layer` / viền vẽ AOI trên {@link MultiHazard2DMap}
 * (`getFillColor` 70, `getLineColor` vàng, `lineWidthMinPixels` 2).
 */
const MAIN_MAP_DRAWN_AOI_FILL = floodRgbColorToCssRgba([234, 179, 8, 70]);
const MAIN_MAP_DRAWN_AOI_STROKE_FILL_OUTLINE = floodRgbColorToCssRgba([250, 204, 21, 140]);
const MAIN_MAP_DRAWN_AOI_LINE = floodRgbColorToCssRgba([250, 204, 21, 255]);
const MAIN_MAP_DRAWN_AOI_LINE_WIDTH = 2;

/** MapLibre: `line-cap` / `line-join` thuộc `layout`, không phải `paint`. */
const LINE_LAYOUT_ROUND = {
  'line-join': 'round' as const,
  'line-cap': 'round' as const,
};

const MIN_MAP_PREVIEW_PX = 200;

export interface RemoteSceneGeoreferencedPreviewProps {
  /** Tiền tố duy nhất cho id source/layer (hai cột baseline/crisis). */
  mapInstanceId: string;
  imageUrl: string;
  aoiPolygon: Polygon;
  tileX: number;
  tileY: number;
  tileZ?: number;
  /** Chiều cao vùng bản đồ (px hoặc chuỗi CSS). */
  mapHeight: number | string;
  /** Nhãn nút mở rộng (chỉ hiện khi có `onRequestExpand`). */
  expandAriaLabel?: string;
  onRequestExpand?: () => void;
  className?: string;
  /** Vùng ngập SAR trong AOI (tùy chọn) — cùng thang màu lớp Flood trên bản đồ chính. */
  floodExtentGeometry?: Polygon | MultiPolygon | null;
  /**
   * Độ sâu danh nghĩa (m) cho thang màu fill vùng ngập. Mặc định: {@link SAR_FLOOD_EXTENT_PREVIEW_NOMINAL_DEPTH_M}.
   * Khi mô phỏng theo mốc thời gian, truyền giá trị thay đổi theo mốc.
   */
  floodNominalDepthM?: number;
  /**
   * Lớp ảnh neo ô (Alpha Earth / GEE). Khi tắt, chỉ hiển thị ranh AOI + vùng ngập (nếu có) trên nền tối.
   * @default true
   */
  showSatelliteRaster?: boolean;
}

/**
 * Ảnh ô tile Alpha Earth (cùng z/x/y với hydrate gallery) neo địa lý + viền AOI — tương thích lớp phân tích trên bản đồ chính.
 */
export function RemoteSceneGeoreferencedPreview({
  mapInstanceId,
  imageUrl,
  aoiPolygon,
  tileX,
  tileY,
  tileZ = SAR_PREVIEW_TILE_ZOOM,
  mapHeight,
  expandAriaLabel = '',
  onRequestExpand,
  className,
  floodExtentGeometry,
  floodNominalDepthM = SAR_FLOOD_EXTENT_PREVIEW_NOMINAL_DEPTH_M,
  showSatelliteRaster = true,
}: RemoteSceneGeoreferencedPreviewProps) {
  const mapRef = useRef<MapRef>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  /**
   * Cùng module `maplibre-gl` (alias VietMap) đã patch `getSky` trong `main.tsx` — phải truyền `mapLib`;
   * nếu không, @vis.gl/react-maplibre gọi `import('maplibre-gl')` async có thể lệch bản → VietMap lỗi lúc load, canvas trống.
   */
  const mapBaseStyle = useMemo(() => {
    if (env.VIETMAP_TILEMAP_KEY) {
      return getVietmapVectorStyleUrl('dm');
    }
    return REMOTE_PREVIEW_MAP_STYLE;
  }, []);
  const imgSourceId = `sar-remote-img-${mapInstanceId}`;
  const imgLayerId = `sar-remote-raster-${mapInstanceId}`;
  const aoiSourceId = `sar-remote-aoi-${mapInstanceId}`;
  const aoiFillId = `sar-remote-aoi-fill-${mapInstanceId}`;
  const aoiLineId = `sar-remote-aoi-line-${mapInstanceId}`;
  const floodSourceId = `sar-remote-flood-${mapInstanceId}`;
  const floodFillId = `sar-remote-flood-fill-${mapInstanceId}`;
  const floodLineGlowId = `sar-remote-flood-line-glow-${mapInstanceId}`;
  const floodLineId = `sar-remote-flood-line-${mapInstanceId}`;

  const previewExtentBbox = useMemo((): ReturnType<typeof turf.bbox> => {
    const tb = webMercatorTileBoundsLngLat(tileX, tileY, tileZ);
    try {
      return turf.bbox(
        turf.featureCollection([
          turf.bboxPolygon([tb.west, tb.south, tb.east, tb.north]),
          turf.feature(aoiPolygon),
        ]),
      );
    } catch {
      return turf.bbox(turf.feature(aoiPolygon));
    }
  }, [tileX, tileY, tileZ, aoiPolygon]);

  /** Bbox giãn 50% mỗi phía để ảnh GEE tràn ra ngoài AOI, phủ kín khung preview. */
  const expandedImageBbox = useMemo((): ReturnType<typeof turf.bbox> => {
    const [w, s, e, n] = previewExtentBbox;
    const dx = (e - w) * 0.5;
    const dy = (n - s) * 0.5;
    return [w - dx, s - dy, e + dx, n + dy];
  }, [previewExtentBbox]);

  const coordinates = useMemo(() => {
    if (showSatelliteRaster && imageUrl.trim()) {
      const [w, s, e, n] = expandedImageBbox;
      return lngLatBboxToImageCoordinates(w, s, e, n);
    }
    return mercatorTileToImageCoordinates(tileX, tileY, tileZ);
  }, [showSatelliteRaster, imageUrl, expandedImageBbox, tileX, tileY, tileZ]);

  const aoiFeature = useMemo(
    (): Feature<Polygon> => ({ type: 'Feature', properties: {}, geometry: aoiPolygon }),
    [aoiPolygon],
  );

  const floodFeature = useMemo((): Feature<Polygon | MultiPolygon> | null => {
    if (!floodExtentGeometry) return null;
    return { type: 'Feature', properties: {}, geometry: floodExtentGeometry };
  }, [floodExtentGeometry]);

  const floodExtentFillRgba = useMemo(() => {
    const d = Number.isFinite(floodNominalDepthM)
      ? floodNominalDepthM
      : SAR_FLOOD_EXTENT_PREVIEW_NOMINAL_DEPTH_M;
    const base = floodRegionPolygonFillRgba(d);
    const boostedAlpha = Math.min(255, Math.round(base[3] * 1.85));
    return [base[0], base[1], base[2], boostedAlpha] as const;
  }, [floodNominalDepthM]);

  const floodExtentFillPaint = useMemo(
    () => ({
      'fill-color': floodRgbColorToCssRgba([
        floodExtentFillRgba[0],
        floodExtentFillRgba[1],
        floodExtentFillRgba[2],
        floodExtentFillRgba[3],
      ]),
      'fill-antialias': true,
    }),
    [floodExtentFillRgba],
  );

  const floodExtentLineGlowPaint = useMemo(
    () => ({
      'line-color': floodRgbColorToCssRgba([56, 189, 248, 255]),
      'line-opacity': 0.35,
      'line-width': 4,
      'line-blur': 2,
    }),
    [],
  );

  const floodExtentLinePaint = useMemo(
    () => ({
      'line-color': floodRgbColorToCssRgba([2, 132, 199, 255]),
      'line-opacity': FLOOD_GEOJSON_STROKE_ALPHA / 255,
      'line-width': 1.5,
      'line-blur': 0.25,
    }),
    [],
  );

  const fitPreviewBounds = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    const [w, s, e, n] = previewExtentBbox;
    // Padding cố định — không phụ thuộc trạng thái raster để toggle ảnh GEE không làm map đổi zoom.
    map.fitBounds(
      [
        [w, s],
        [e, n],
      ],
      { padding: 36, duration: 0, maxZoom: 18 },
    );
  }, [previewExtentBbox]);

  const scheduleResizeAndFit = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    // Trong modal / tab / khối `maxHeight`, lần đầu kích thưỡc canvas thường 0 — phải resize trước khi fitBounds thì vùng vẽ mới thấy đúng.
    map.resize();
    fitPreviewBounds();
  }, [fitPreviewBounds]);

  const handleLoad = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    scheduleResizeAndFit();
    map.once('idle', () => {
      scheduleResizeAndFit();
    });
    queueMicrotask(() => {
      scheduleResizeAndFit();
    });
    requestAnimationFrame(() => {
      scheduleResizeAndFit();
    });
    window.setTimeout(() => {
      scheduleResizeAndFit();
    }, 100);
  }, [scheduleResizeAndFit]);

  useEffect(() => {
    scheduleResizeAndFit();
  }, [scheduleResizeAndFit, imageUrl, showSatelliteRaster]);

  useLayoutEffect(() => {
    let innerRaf = 0;
    const outerRaf = requestAnimationFrame(() => {
      innerRaf = requestAnimationFrame(() => {
        scheduleResizeAndFit();
      });
    });
    return () => {
      cancelAnimationFrame(outerRaf);
      cancelAnimationFrame(innerRaf);
    };
  }, [scheduleResizeAndFit]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      scheduleResizeAndFit();
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, [scheduleResizeAndFit]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            scheduleResizeAndFit();
          }
        }
      },
      { root: null, threshold: 0, rootMargin: '0px' },
    );
    io.observe(el);
    return () => {
      io.disconnect();
    };
  }, [scheduleResizeAndFit]);

  const containerStyle = useMemo((): { height: number | string; minHeight?: number } => {
    if (typeof mapHeight === 'number') {
      return { height: Math.max(MIN_MAP_PREVIEW_PX, mapHeight) };
    }
    return { height: mapHeight, minHeight: MIN_MAP_PREVIEW_PX };
  }, [mapHeight]);

  return (
    <div
      ref={containerRef}
      className={`relative w-full min-h-[200px] overflow-hidden rounded-lg border border-[var(--glass-border)] bg-[#0b1220] ${className ?? ''}`}
      style={containerStyle}
    >
      <Map
        key={mapInstanceId}
        ref={mapRef}
        /* react-map-gl MapLib vs default export: runtime OK, TS 5.6+ can disagree */
        mapLib={maplibregl as never}
        mapStyle={mapBaseStyle}
        initialViewState={{
          longitude: MAP_DEFAULTS.CENTER.lng,
          latitude: MAP_DEFAULTS.CENTER.lat,
          zoom: 10,
        }}
        style={{ width: '100%', height: '100%' }}
        minZoom={MAP_DEFAULTS.MIN_ZOOM}
        maxZoom={MAP_DEFAULTS.MAX_ZOOM}
        attributionControl={false}
        onError={(e) => {
          console.warn('[RemoteSceneGeoreferencedPreview]', e.error);
        }}
        onLoad={handleLoad}
      >
        {/* AOI/flood add TRƯỚC image source (mount-order) để có thể dùng `beforeId` chèn image xuống dưới. */}
        {floodFeature ? (
          <Source key={floodSourceId} id={floodSourceId} type="geojson" data={floodFeature}>
            <Layer id={floodFillId} type="fill" paint={floodExtentFillPaint} />
            <Layer
              id={floodLineGlowId}
              type="line"
              layout={LINE_LAYOUT_ROUND}
              paint={floodExtentLineGlowPaint}
            />
            <Layer
              id={floodLineId}
              type="line"
              layout={LINE_LAYOUT_ROUND}
              paint={floodExtentLinePaint}
            />
          </Source>
        ) : null}
        <Source key={aoiSourceId} id={aoiSourceId} type="geojson" data={aoiFeature}>
          <Layer
            id={aoiFillId}
            type="fill"
            paint={{
              'fill-color': MAIN_MAP_DRAWN_AOI_FILL,
              'fill-outline-color': MAIN_MAP_DRAWN_AOI_STROKE_FILL_OUTLINE,
            }}
          />
          <Layer
            id={aoiLineId}
            type="line"
            layout={LINE_LAYOUT_ROUND}
            paint={{
              'line-color': MAIN_MAP_DRAWN_AOI_LINE,
              'line-width': MAIN_MAP_DRAWN_AOI_LINE_WIDTH,
              'line-opacity': 1,
              'line-blur': 0.25,
            }}
          />
        </Source>
        {showSatelliteRaster && imageUrl.trim() ? (
          <Source
            key={imgSourceId}
            id={imgSourceId}
            type="image"
            url={imageUrl}
            coordinates={coordinates}
          >
            <Layer
              id={imgLayerId}
              type="raster"
              beforeId={floodFeature ? floodFillId : aoiFillId}
              paint={{ 'raster-opacity': 1, 'raster-fade-duration': 0 }}
            />
          </Source>
        ) : null}
      </Map>
      {onRequestExpand ? (
        <button
          type="button"
          className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-md border border-white/15 bg-black/45 text-slate-100 shadow-md backdrop-blur-sm transition hover:bg-black/55 focus:outline-none focus:ring-2 focus:ring-sky-400/50"
          aria-label={expandAriaLabel.trim() || 'Expand'}
          onClick={onRequestExpand}
        >
          <Maximize2 size={16} strokeWidth={2} aria-hidden />
        </button>
      ) : null}
    </div>
  );
}
